import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications, useStore } from '../store/useStore';
import { useAuth } from './useAuth';
import { eventiService } from '../lib/eventi/eventiService';
import {
  EVENTO_BIGLIETTI_VISTI,
  caricaStato,
  daAvvisare,
  salvaStato,
  testoAvviso,
  type OrdineArrivato,
} from '../lib/eventi/avvisiBiglietti';
import { APP_ICON } from '../lib/brand';

const AUDIO_PATH = '/sounds/reminder.mp3';
// Gli ordini si leggono dal sito ogni 10 minuti: controllare ogni minuto
// basta perche' l'avviso arrivi subito dopo la lettura.
const POLL_INTERVAL_MS = 60 * 1000;
const NOTIFICATION_ID = 'biglietti-nuovi';

/** Chi vede la pagina Eventi (e, dalla migrazione 036, le tabelle dei biglietti). */
const RUOLI_EVENTI: readonly string[] = ['admin', 'commerciale'];

/**
 * Avviso "qualcuno ha comprato biglietti sul sito": pallino con il numero
 * sulla voce Eventi, suono, notifica del browser e avviso in app con il
 * pulsante per aprire la scheda Biglietti. Il pallino si azzera aprendo la
 * scheda Biglietti (segnaBigliettiVisti).
 *
 * Funziona con l'app aperta, come gli avvisi dei lead e delle fatture; un
 * ordine arrivato ad app chiusa fa il pallino (e suona) alla riapertura.
 */
export const useBigliettiAlerts = () => {
  const { addNotification, removeNotification } = useNotifications();
  const setNewTicketOrderCount = useStore((s) => s.setNewTicketOrderCount);
  const navigate = useNavigate();
  const { profile } = useAuth();
  const puoVedere = !!profile?.role && RUOLI_EVENTI.includes(profile.role);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  // true quando il browser ha bloccato il suono: parte al primo gesto.
  const suonoInSospesoRef = useRef(false);

  useEffect(() => {
    const audio = new Audio(AUDIO_PATH);
    audio.preload = 'auto';
    audioRef.current = audio;
  }, []);

  const suona = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = 0;
    audio
      .play()
      .then(() => {
        suonoInSospesoRef.current = false;
      })
      .catch(() => {
        suonoInSospesoRef.current = true;
      });
  }, []);

  // Il browser non lascia suonare una pagina finche' l'utente non ha cliccato
  // o premuto un tasto: il suono bloccato parte al primo gesto.
  useEffect(() => {
    const alGesto = () => {
      if (suonoInSospesoRef.current) suona();
    };
    window.addEventListener('pointerdown', alGesto);
    window.addEventListener('keydown', alGesto);
    return () => {
      window.removeEventListener('pointerdown', alGesto);
      window.removeEventListener('keydown', alGesto);
    };
  }, [suona]);

  useEffect(() => {
    if (!puoVedere) {
      setNewTicketOrderCount(0);
      return;
    }
    let annullato = false;

    const apri = (eventoId: string | null) => {
      removeNotification(NOTIFICATION_ID);
      navigate(eventoId ? `/eventi/${eventoId}?scheda=biglietti` : '/eventi');
    };

    const avvisa = (nuovi: OrdineArrivato[]) => {
      const messaggio = testoAvviso(nuovi);
      const eventoId = nuovi[nuovi.length - 1].eventoId;
      suona();
      if ('Notification' in window && Notification.permission === 'granted') {
        const notifica = new Notification('Biglietti comprati sul sito', { body: messaggio, icon: APP_ICON, tag: NOTIFICATION_ID });
        notifica.onclick = () => {
          window.focus();
          apri(eventoId);
          notifica.close();
        };
      }
      // Un solo avviso in app, aggiornato: non uno nuovo per ogni ordine.
      removeNotification(NOTIFICATION_ID);
      addNotification({
        id: NOTIFICATION_ID,
        type: 'success',
        title: 'Biglietti comprati sul sito',
        message: messaggio,
        persistent: true,
        action: { label: 'Apri biglietti', onClick: () => apri(eventoId) },
      });
    };

    const controlla = async () => {
      try {
        const stato = caricaStato();
        const arrivati = await eventiService.ordiniArrivatiDopo(stato.vistoFino);
        if (annullato) return;
        setNewTicketOrderCount(arrivati.length);
        if (arrivati.length === 0) {
          removeNotification(NOTIFICATION_ID);
          return;
        }
        // Si rilegge lo stato: un'altra scheda del browser potrebbe aver gia'
        // avvisato (o segnato visti) mentre questa aspettava la risposta.
        const { nuovi, stato: dopo } = daAvvisare(arrivati, caricaStato());
        if (nuovi.length === 0) return;
        salvaStato(dopo);
        avvisa(nuovi);
      } catch {
        /* non bloccante: il pallino resta com'era fino al prossimo controllo */
      }
    };

    // La scheda Biglietti aperta (qui o in un'altra scheda del browser): si
    // ricontrolla subito, cosi' il pallino si azzera senza aspettare.
    const alCambioDiStato = (e: Event) => {
      if (e instanceof StorageEvent && e.key !== null && !e.key.startsWith('pixel-slab-biglietti')) return;
      void controlla();
    };

    void controlla();
    const intervallo = window.setInterval(() => void controlla(), POLL_INTERVAL_MS);
    window.addEventListener(EVENTO_BIGLIETTI_VISTI, alCambioDiStato);
    window.addEventListener('storage', alCambioDiStato);
    return () => {
      annullato = true;
      window.clearInterval(intervallo);
      window.removeEventListener(EVENTO_BIGLIETTI_VISTI, alCambioDiStato);
      window.removeEventListener('storage', alCambioDiStato);
    };
  }, [puoVedere, addNotification, removeNotification, navigate, suona, setNewTicketOrderCount]);
};

export default useBigliettiAlerts;
